import { useState, useEffect, useCallback } from 'react';
import {
  Button
} from '@etherisc/ui-kit';
import type { AdministrativeUnit, AdminUnitLoadingFactor } from './LoadingFactorsTable';
import { ProgressiveAdminUnitSelector } from './ProgressiveAdminUnitSelector';

interface AddAdminUnitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (adminUnit: AdministrativeUnit) => void;
  existingFactors: AdminUnitLoadingFactor[];
}

export function AddAdminUnitDialog({
  isOpen,
  onClose,
  onAdd,
  existingFactors
}: AddAdminUnitDialogProps) {
  const [selectedUnit, setSelectedUnit] = useState<AdministrativeUnit | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when dialog closes
      setSelectedUnit(null);
    }
  }, [isOpen]);

  // Check if the selected unit already exists in the table
  const existingAdminUnitIds = new Set(existingFactors.map(f => f.adminUnitId));
  const isAlreadyAdded = selectedUnit && existingAdminUnitIds.has(selectedUnit.id);

  const handleSelectionChange = useCallback((unit: AdministrativeUnit | null) => {
    setSelectedUnit(unit);
  }, []);

  const handleAdd = () => {
    if (!selectedUnit || isAlreadyAdded) return;
    onAdd(selectedUnit);
    onClose();
  };

  const canAdd = selectedUnit && !isAlreadyAdded;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Add Administrative Unit</h2>
          <p className="text-gray-600 mt-2">
            Select an administrative unit to add to the loading factors table. 
            You can select at any level - country, province, district, or ward.
            Loading factors for all perils will be initialized to 0% and can be edited directly in the table.
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Progressive Administrative Unit Selector */}
          <ProgressiveAdminUnitSelector
            key="au-selector"
            countryCode="ZW"
            onSelectionChange={handleSelectionChange}
          />

          {/* Already Added Warning */}
          {isAlreadyAdded && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="text-sm font-medium text-amber-800">
                This administrative unit is already in the loading factors table.
              </div>
              <div className="text-amber-700 text-sm mt-1">
                Please select a different administrative unit.
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleAdd}
            disabled={!canAdd}
          >
            Add Administrative Unit
          </Button>
        </div>
      </div>
    </div>
  );
}
